import { z } from 'zod';

// RCF reference data (T-113) — the Settings-maintained inputs the Resource
// Change Form (T-115) draws on. See design doc O6.

// ── Cigna principals (Sam / Chris) ───────────────────────────────────
// One entity, two RCF roles: `email` is the manager's-principal recipient on
// the RCF email; `name` is the expense-center value on the RCF file.
export const CignaPrincipal = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
});
export type CignaPrincipal = z.infer<typeof CignaPrincipal>;

export const CignaPrincipalInput = z.object({
  name: z.string().min(1).max(120),
  email: z.string().email(),
});
export type CignaPrincipalInput = z.infer<typeof CignaPrincipalInput>;

export const CignaPrincipalList = z.array(CignaPrincipal);

export const CignaPrincipalsResponse = z.object({ principals: CignaPrincipalList });
export type CignaPrincipalsResponse = z.infer<typeof CignaPrincipalsResponse>;

// ── Cigna managers ───────────────────────────────────────────────────
// A Cigna manager a resource reports to, rolling up to one principal. The
// principal is nullable: removing a principal (SET NULL) leaves the manager
// flagged as needing reassignment rather than deleting it.
export const CignaManager = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
  principal: CignaPrincipal.nullable(),
});
export type CignaManager = z.infer<typeof CignaManager>;

export const CignaManagerInput = z.object({
  name: z.string().min(1).max(120),
  email: z.string().email(),
  principal_id: z.string().uuid().nullable().optional(),
});
export type CignaManagerInput = z.infer<typeof CignaManagerInput>;

export const CignaManagerList = z.array(CignaManager);

export const CignaManagersResponse = z.object({ managers: CignaManagerList });
export type CignaManagersResponse = z.infer<typeof CignaManagersResponse>;

// ── Internal uIT RCF recipients (T-144) ──────────────────────────────
// The uIT people CC'd on every RCF email — a managed list replacing the old
// hardcoded francine/eric singletons (app_settings is flat scalars; lists are
// tables here, like cigna_managers).
export const RcfInternalRecipient = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string().email(),
});
export type RcfInternalRecipient = z.infer<typeof RcfInternalRecipient>;

export const RcfInternalRecipientInput = z.object({
  name: z.string().max(120).optional(),
  email: z.string().email(),
});
export type RcfInternalRecipientInput = z.infer<typeof RcfInternalRecipientInput>;

export const RcfInternalRecipientList = z.array(RcfInternalRecipient);
export const RcfInternalRecipientsResponse = z.object({ recipients: RcfInternalRecipientList });
export type RcfInternalRecipientsResponse = z.infer<typeof RcfInternalRecipientsResponse>;

// Review-and-send (T-144): the review screen picks the Cigna manager, then sends.
// Optional so a re-send can omit it (the manager is already persisted).
export const RcfSendInput = z.object({ cigna_manager_id: z.string().uuid().optional() });
export type RcfSendInput = z.infer<typeof RcfSendInput>;

// ── RCF singletons: SOW, fixed recipients, email template ────────────
// WAND = Cigna's contingent-labor support shared mailbox (Evernorth, Q4).
// Francine + Eric are fixed uIT recipients. The manager + their principal are
// added per-RCF (T-115), not stored here.
export const RcfSettings = z.object({
  sow_number: z.string(),
  // Editable RCF-file constants (T-115): the Cigna Company Code and the Location
  // address cell — same across resources for the current project.
  company_code: z.string(),
  location_address: z.string(),
  wand_email: z.string(),
  email_subject: z.string(),
  email_body: z.string(),
});
export type RcfSettings = z.infer<typeof RcfSettings>;

// Update is partial — Settings sections PATCH only the fields they own.
export const RcfSettingsUpdate = z
  .object({
    sow_number: z.string().min(1).max(40),
    company_code: z.string().max(120),
    location_address: z.string().max(200),
    wand_email: z.string().email(),
    email_subject: z.string().min(1).max(200),
    email_body: z.string().min(1).max(20000),
  })
  .partial();
export type RcfSettingsUpdate = z.infer<typeof RcfSettingsUpdate>;

// Defaults applied when a key is unset. WAND seeded from Q4; the markdown
// template mirrors the onboarding-email renderer (placeholders substituted
// after render). Placeholders: resourceName, startDate, resourceAddress,
// resourcePhone, uitEmail.
export const RCF_SETTINGS_DEFAULTS: RcfSettings = {
  sow_number: '5',
  company_code: '',
  location_address: '',
  wand_email: 'contingentlaborsupport@evernorth.com',
  email_subject: 'RCF — {{resourceName}} starting {{startDate}}',
  // Eric's verbatim body (T-115). Placeholders substituted after render.
  email_body: `Hi Team..

See attached RCF for onboarding of:

{{resourceName}}
Start date: {{startDate}}
Phone Number: {{resourcePhone}}
Mailing Address: {{resourceAddress}}

Please ensure {{resourceName}} is set up to require selection of a project code when submitting timesheets.


Thanks`,
};

export const RCF_EMAIL_PLACEHOLDERS = [
  'resourceName',
  'startDate',
  'resourceAddress',
  'resourcePhone',
  'uitEmail',
] as const;
